import { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarRange, FileText, Pencil, ShieldAlert, Target, Wallet } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { HEALTH_LABEL, type HealthLevel, type PhaseHealth } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { cn, fmtWindow } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/field';
import { Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';
import { HealthBadge, HealthDot } from '@/components/tracker/bits';
import { ProgressReview } from '@/components/ai/Assistant';
import {
  ChartCard,
  CriteriaBar,
  NeedsYou,
  RiskPicture,
  SpendChart,
  Timeline,
  type SpendData,
} from '@/components/executive/Charts';

interface Progress {
  done: number;
  total: number;
}
interface Exec {
  today: string;
  health: { program: HealthLevel; narrative: string | null; phasesInFlight: number; paProgress: Progress };
  canEditNarrative: boolean;
  phases: Array<{
    id: string;
    number: number;
    name: string;
    plannedStart: string;
    plannedEnd: string;
    forecastStart: string;
    forecastEnd: string;
    health: PhaseHealth;
    taskProgress: Progress;
    deliverableProgress: Progress;
    inFlight: boolean;
    milestones: Array<{ id: string; name: string; planned: string; forecast: string; achieved: string | null }>;
  }>;
  risk: {
    heat: number[][];
    risks: { HIGH: number; MEDIUM: number; LOW: number; UNSCORED: number };
    issues: { CRITICAL: number; HIGH: number; MEDIUM: number; LOW: number };
  };
  decisions: Array<{ id: string; ref: string; title: string; due: string | null; overdue: boolean }>;
  awaiting: Array<{ id: string; name: string; phase: number | null; version: number; days: number | null }>;
  latestWeekly: { id: string; label: string | null; version: number; publishedAt: string | null } | null;
  budget: SpendData | null;
}
interface Criterion {
  id: string;
  group: string;
  name: string;
  target: string;
  measurableFrom: { number: number; name: string } | null;
  measurableNote: string | null;
  status: 'NOT_YET_MEASURABLE' | 'ON_TRACK' | 'AT_RISK' | 'ACHIEVED';
  currentValue: string | null;
}

const HEALTH_BG: Record<HealthLevel, string> = {
  GREEN: 'border-ok/30 bg-ok-soft',
  AMBER: 'border-warn/30 bg-warn-soft',
  RED: 'border-bad/30 bg-bad-soft',
};

export function ExecutivePage() {
  const program = useOutletContext<Program>();
  const base = `/api/programs/${program.id}`;
  const q = useQuery({ queryKey: ['executive', program.id], queryFn: () => api.get<Exec>(`${base}/executive`) });
  const criteria = useQuery({
    queryKey: ['criteria', program.id],
    queryFn: () => api.get<Criterion[]>(`${base}/criteria`),
  });
  const p = (path: string) => `/p/${program.id}/${path}`;

  if (!q.data) return <Spinner className="h-64" />;
  const d = q.data;

  return (
    <div className="mx-auto max-w-[1280px] space-y-5 px-6 py-6">
      {/* 1. Program health */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border px-5 py-4',
          HEALTH_BG[d.health.program],
        )}
      >
        <div className="flex items-center gap-2.5">
          <HealthDot level={d.health.program} className="size-3.5" />
          <span className="text-lg font-semibold">Program {HEALTH_LABEL[d.health.program].toLowerCase()}</span>
        </div>
        <Narrative program={program} value={d.health.narrative} canEdit={d.canEditNarrative} />
        <div className="ml-auto flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <span>
            <span className="font-semibold tabular-nums">
              {d.health.paProgress.done} of {d.health.paProgress.total}
            </span>{' '}
            <span className="text-muted">PA §4 deliverables accepted</span>
          </span>
          {d.latestWeekly ? (
            <Link
              to={p(`reports/${d.latestWeekly.id}`)}
              className="inline-flex items-center gap-1.5 font-medium text-brand-text hover:underline"
            >
              <FileText className="size-4" /> Weekly report, {d.latestWeekly.label}
            </Link>
          ) : (
            <span className="text-muted">No weekly report published yet</span>
          )}
          <ProgressReview program={program} label="Review program progress" />
        </div>
      </div>

      {/* 2. Phase cards */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {d.phases.map((ph) => (
          <Link
            key={ph.id}
            to={p(`tracker/phase/${ph.id}`)}
            className={cn(
              'group rounded-lg border bg-surface p-3.5 transition hover:border-border-strong hover:shadow-sm',
              ph.inFlight ? 'border-border-strong' : 'border-border',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs font-semibold text-muted">Phase {ph.number}</span>
              <HealthBadge health={ph.health} />
            </div>
            <p className="mt-1 line-clamp-2 min-h-[2.5em] text-[13.5px] font-medium leading-tight">{ph.name}</p>
            <p className="mt-1.5 text-xs text-muted">
              {fmtWindow(ph.forecastStart, ph.forecastEnd)}
              {ph.forecastEnd > ph.plannedEnd && <span className="text-bad"> · past PA window</span>}
            </p>
            <Bar label="Tasks" p={ph.taskProgress} />
            <Bar label="Deliverables" p={ph.deliverableProgress} />
            {ph.health.reasons.length > 0 && !ph.health.overridden && (
              <p className="mt-2 line-clamp-2 text-[11px] text-muted">{ph.health.reasons.join('; ')}</p>
            )}
          </Link>
        ))}
      </div>

      {/* 3. What needs the committee */}
      <NeedsYou
        programId={program.id}
        clientShortName={program.clientShortName}
        decisions={d.decisions}
        awaiting={d.awaiting}
      />

      {/* 4. Timeline against the PA plan */}
      <ChartCard
        title="Timeline against the PA plan"
        icon={<CalendarRange />}
        action={
          <Link to={p('tracker')} className="text-brand-text hover:underline">
            Roadmap
          </Link>
        }
      >
        <Timeline phases={d.phases} today={d.today} />
      </ChartCard>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* 5. Risk picture */}
        <ChartCard title="Risks and issues" icon={<ShieldAlert />} tone="bad">
          <RiskPicture programId={program.id} heat={d.risk.heat} risks={d.risk.risks} issues={d.risk.issues} />
        </ChartCard>

        {/* 6. Spend over time, for members with budget access; otherwise success criteria take the row */}
        {d.budget ? (
          <ChartCard
            title="Implementation spend"
            icon={<Wallet />}
            tone="ok"
            className="lg:col-span-2"
            action={
              <Link to={p('budget')} className="text-brand-text hover:underline">
                Budget
              </Link>
            }
          >
            <SpendChart programId={program.id} b={d.budget} />
          </ChartCard>
        ) : (
          <ChartCard title="Success criteria" className="lg:col-span-2" icon={<Target />} tone="info">
            <CriteriaBar programId={program.id} statuses={criteria.data?.map((c) => c.status)} />
          </ChartCard>
        )}
      </div>

      {/* 7. Success criteria, below the spend chart for budget-access members */}
      {d.budget && (
        <ChartCard title="Success criteria" icon={<Target />} tone="info">
          <CriteriaBar programId={program.id} statuses={criteria.data?.map((c) => c.status)} />
        </ChartCard>
      )}
    </div>
  );
}

function Bar({ label, p }: { label: string; p: Progress }) {
  const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
  return (
    <div className="mt-2">
      <div className="flex justify-between text-[11px] text-muted">
        <span>{label}</span>
        <span className="tabular-nums">
          {p.done} of {p.total}
        </span>
      </div>
      <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-surface-2">
        <div className="h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Narrative({ program, value, canEdit }: { program: Program; value: string | null; canEdit: boolean }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? '');
  const qc = useQueryClient();
  const toast = useToast();
  const save = useMutation({
    mutationFn: () => api.patch(`/api/programs/${program.id}/executive/narrative`, { healthNarrative: v || null }),
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['executive', program.id] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (editing)
    return (
      <form
        className="flex min-w-[280px] flex-1 gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Input
          value={v}
          onChange={(e) => setV(e.target.value)}
          maxLength={280}
          autoFocus
          aria-label="Health narrative"
        />
        <Button type="submit" size="sm" loading={save.isPending}>
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </form>
    );
  return (
    <p className="flex min-w-[260px] flex-1 items-center gap-2 text-sm">
      <span className={value ? '' : 'text-muted'}>
        {value || (canEdit ? 'Add a one-line status for the committee.' : '')}
      </span>
      {canEdit && (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Edit health narrative"
          onClick={() => (setV(value ?? ''), setEditing(true))}
        >
          <Pencil />
        </Button>
      )}
    </p>
  );
}
